import { Intersection } from '@api/api/ontology';
import { ThemeSchemaResponse } from '@api/api/theme-overview';
import {
  countDirections,
  getIntersectionEdges,
} from '@api/utils/ontology-utils';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { capitalizeFirstLetter } from '@helpers/utils/strings/strings';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useGraphIntersections } from '@terminal/react-query-hooks/graph/useGraphIntersections';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { RelatedThemesTable } from '@terminal/views/theme-overview/tab-content/pulse-overview/components/related-themes/RelatedThemesTable';
import { IndustryThemeDrawer } from '@terminal/widgets/financial-macro-theme-to-companies/IndustryThemeDrawer';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styles from './IndustryThemesSection.module.scss';

interface IndustryThemesSectionProps {
  theme: ThemeSchemaResponse;
  isCompact?: boolean;
}

export const IndustryThemesSection = ({
  theme,
  isCompact = false,
}: IndustryThemesSectionProps) => {
  const { t } = useTranslation(['widget', 'common']);
  const { setQueryParams } = useQueryParams();
  const { navigateToTheme } = useNavigateToThemeOverview();
  const { trackThemeToThemeDrawerOpen } = usePosthogEvents();

  const { data, isLoading, isError } = useGraphIntersections({
    node: theme.id,
    nodeType: 'theme',
    targetRelation: 'industry',
  });

  const tableData =
    data?.intersections.slice(0, 5).map((intersection: Intersection, index) => {
      const edges = getIntersectionEdges(intersection);
      const directionCounts = countDirections(edges);
      const label = data?.nodes.find(node => node.key === intersection.node)
        ?.attributes.label;

      return {
        rank: index + 1,
        themeId: intersection.node.split('/')[1],
        name: label ?? '',
        companiesCount: directionCounts.total,
        positiveCount: directionCounts.positive,
        negativeCount: directionCounts.negative,
        mixedCount: directionCounts.neutral,
      };
    }) ?? [];

  const handleThemeClick = (themeId: string) => {
    if (isCompact) {
      navigateToTheme({
        themeId,
        source: POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW_DRAWER,
      });
    } else {
      trackThemeToThemeDrawerOpen(POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW, [
        themeId,
      ]);
      setQueryParams({
        [queryKeys.activeDrawer]: queryValues.industryThemeDrawer,
        [queryKeys.industryThemeId]: themeId,
      });
    }
  };

  const totalCount = data?.intersections.length ?? 0;

  if (isLoading) {
    return (
      <SkeletonLoader
        className={clsx(styles.skeletonWrapper, {
          [styles.compactSkeleton]: isCompact,
        })}
        areas={[
          {
            styles: {
              width: '100%',
              height: '100%',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (isError) {
    throw new Error('IndustryThemesSection: failed to fetch industry themes');
  }

  if (!totalCount) {
    return (
      <div className={styles.root}>
        <ErrorMessageWrapper variant="empty" showBorder={false} />
      </div>
    );
  }

  const title = isCompact
    ? capitalizeFirstLetter(
        t('widget:themeOverview.tabs.overview.industryThemes.titleCount', {
          count: tableData.length,
        }).toLowerCase()
      )
    : t('widget:themeOverview.tabs.overview.industryThemes.title');

  return (
    <div
      className={clsx(styles.root, {
        [styles.compact]: isCompact,
      })}
    >
      <div className={styles.titleWrapper}>
        <div className={clsx({ [styles.sectionTitle]: !isCompact })}>
          <SectionTitle
            title={title}
            variant={isCompact ? 'small' : 'default'}
          />
        </div>
        <div className={styles.subtitleLabel}>
          {t('widget:themeOverview.tabs.overview.industryThemes.subtitle', {
            topThemes: tableData.length,
            totalThemes: totalCount,
          })}
        </div>
      </div>
      <RelatedThemesTable
        themes={tableData}
        themeName={theme.name}
        themeColumnLabel={t(
          'widget:themeOverview.tabs.overview.industryThemes.industryTheme'
        )}
        themeType="industry"
        onThemeClick={handleThemeClick}
        dataTestId="industry-themes-table"
        isCompact={isCompact}
      />
      {!isCompact && (
        <IndustryThemeDrawer
          theme={theme}
          intersections={data?.intersections}
        />
      )}
    </div>
  );
};
